import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
  MdAdd, MdEdit, MdDelete, MdClose, MdToggleOn, MdToggleOff, MdFolder, MdArrowBack,
  MdVisibility, MdOpenInNew, MdCalendarMonth, MdFormatListBulleted,
} from 'react-icons/md';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';

const todayKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const formatDateLabel = (dateKey) => {
  // Dates are required going forward — this only shows up for old rows saved before that.
  if (dateKey === 'evergreen') return '⚠️ No date set (needs fixing)';
  const label = new Date(`${dateKey}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
  return dateKey === todayKey() ? `Today · ${label}` : label;
};

/* ------------------------------------------------------------------ */
/* Phone-ratio preview of the quote card design (2160 x 4200)          */
/* ------------------------------------------------------------------ */
function QuotePreview({ imageUrl, width = 150 }) {
  const height = Math.round((width * 4200) / 2160); // 2160 x 4200
  return (
    <div
      style={{
        position: 'relative', width, height, borderRadius: 12, overflow: 'hidden',
        background: '#111', flexShrink: 0,
      }}
    >
      {imageUrl
        ? <img src={imageUrl} alt="design" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 11, textAlign: 'center', padding: 8 }}>
            <span>No design</span>
            <span style={{ fontSize: 10, color: '#666', marginTop: 4 }}>2160 × 4200</span>
          </div>}
    </div>
  );
}

// Helper: validate natural image dimensions strictly to 2160 x 4200 px
const validateQuoteImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const isExact = img.naturalWidth === 2160 && img.naturalHeight === 4200;
      resolve({ valid: isExact, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image file.'));
    };
    img.src = objectUrl;
  });
};

/* ------------------------------------------------------------------ */
const emptyForm = { title: '', image_url: '', thumbnail_url: '', scheduled_date: '', sort_order: '', is_active: true };

export default function QuotesManager() {
  const [folders, setFolders] = useState([]); // [{date_key, count}]
  const { page: foldersPage, limit: foldersLimit, setPage: setFoldersPage, setLimit: setFoldersLimit } = usePagination(20);
  const [foldersPagination, setFoldersPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null); // dateKey or null = folder list view
  const [folderQuotes, setFolderQuotes] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [viewQuote, setViewQuote] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' (all quotes list view) | 'folders'
  const [allQuotes, setAllQuotes] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allTotal, setAllTotal] = useState(0);

  const handleStatusChange = (val) => {
    setFilterStatus(val);
    setFoldersPage(1);
  };

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: foldersPage, limit: foldersLimit };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/admin/quotes/folders', { params });
      setFolders(res.data.data || []);
      setFoldersPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch { toast.error('Failed to load quote folders'); }
    finally { setLoading(false); }
  }, [foldersPage, foldersLimit, filterStatus]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const fetchAllQuotes = useCallback(async () => {
    setAllLoading(true);
    try {
      const params = { limit: 200 };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/admin/quotes', { params });
      setAllQuotes(res.data.data || []);
      setAllTotal(res.data.total || (res.data.data ? res.data.data.length : 0));
    } catch {
      toast.error('Failed to load quotes');
    } finally {
      setAllLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchAllQuotes();
  }, [fetchAllQuotes]);

  const fetchFolderQuotes = useCallback(async (dateKey) => {
    if (!dateKey) return;
    setFolderLoading(true);
    try {
      const params = { date: dateKey, limit: 200 };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/admin/quotes', { params });
      setFolderQuotes(res.data.data || []);
    } catch { toast.error('Failed to load quotes'); }
    finally { setFolderLoading(false); }
  }, [filterStatus]);

  useEffect(() => {
    if (selectedFolder) fetchFolderQuotes(selectedFolder);
  }, [selectedFolder, fetchFolderQuotes]);

  // Refresh whichever view is on screen
  const refreshCurrent = useCallback(() => {
    fetchFolders();
    fetchAllQuotes();
    if (selectedFolder) fetchFolderQuotes(selectedFolder);
  }, [fetchFolders, fetchAllQuotes, selectedFolder, fetchFolderQuotes]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const uploadDesign = (isEdit = false) => {
    const isEditing = isEdit === true;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Strict dimension check: exactly 2160 × 4200 px
      try {
        const { valid, width, height } = await validateQuoteImage(file);
        if (!valid) {
          toast.error(`Invalid dimensions (${width} × ${height} px). Quote image must be exactly 2160 × 4200 px.`, {
            duration: 6000,
            icon: '⚠️',
          });
          return;
        }
      } catch {
        toast.error('Could not verify image dimensions. Please upload a valid image file.');
        return;
      }

      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
        const main = await api.post('/admin/upload/quote-image', fd, cfg);
        let thumbUrl = '';
        try {
          const fd2 = new FormData(); fd2.append('file', file);
          const thumb = await api.post('/admin/upload/quote-thumbnail', fd2, cfg);
          thumbUrl = thumb.data?.data?.url || thumb.data?.url || '';
        } catch { /* thumbnail optional */ }

        const uploadedUrl = main.data?.data?.url || main.data?.url;
        const finalThumbUrl = thumbUrl || uploadedUrl;

        if (isEditing) {
          setEditingQuote(p => p ? ({ ...p, image_url: uploadedUrl, thumbnail_url: finalThumbUrl }) : null);
          toast.success('Design updated (2160 × 4200 px)');
        } else {
          setForm(p => ({ ...p, image_url: uploadedUrl, thumbnail_url: finalThumbUrl }));
          toast.success('Design uploaded (2160 × 4200 px)');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingQuote) return;
    if (!editingQuote.image_url) return toast.error('Upload a design');
    if (!editingQuote.scheduled_date) return toast.error('Pick a date');
    if (editingQuote.scheduled_date.slice(0, 10) < todayKey()) {
      return toast.error('Cannot schedule quote for a past date. Please select today or a future date.');
    }
    setSavingEdit(true);
    try {
      await api.put(`/admin/quotes/${editingQuote.id}`, {
        title: editingQuote.title || null,
        image_url: editingQuote.image_url,
        thumbnail_url: editingQuote.thumbnail_url || null,
        scheduled_date: editingQuote.scheduled_date || null,
        sort_order: Number(editingQuote.sort_order) || 0,
        is_active: editingQuote.is_active,
      });
      toast.success('Quote updated successfully');
      setEditingQuote(null);
      refreshCurrent();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update quote');
    } finally {
      setSavingEdit(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.image_url) return toast.error('Upload a design');
    if (!form.scheduled_date) return toast.error('Pick a date');
    if (form.scheduled_date < todayKey()) {
      return toast.error('Cannot schedule quote for a past date. Please select today or a future date.');
    }
    try {
      await api.post('/admin/quotes', {
        title: form.title || null,
        image_url: form.image_url,
        thumbnail_url: form.thumbnail_url || null,
        scheduled_date: form.scheduled_date || null,
        sort_order: Number(form.sort_order) || 0,
        is_active: form.is_active,
      });
      toast.success('Quote added');
      setForm(emptyForm);
      setShowForm(false);
      refreshCurrent();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const toggle = async (q) => {
    try { await api.put(`/admin/quotes/${q.id}`, { is_active: !q.is_active }); refreshCurrent(); }
    catch { toast.error('Toggle failed'); }
  };

  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    const deletedId = deleteModal.id;
    setDeleting(true);
    try {
      await api.delete(`/admin/quotes/${deletedId}`);
      toast.success('Quote deleted successfully.');
      setDeleteModal(null);
      setAllQuotes(prev => prev.filter(q => q.id !== deletedId));
      setFolderQuotes(prev => prev.filter(q => q.id !== deletedId));
      refreshCurrent();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const renderQuoteCard = (q) => {
    let statusBadge = null;
    if (q.status === 'Scheduled') statusBadge = <span className="badge" style={{ background: '#FEF08A', color: '#854D0E', fontSize: 11 }}>🟡 Scheduled</span>;
    else if (q.status === 'Live') statusBadge = <span className="badge" style={{ background: '#BBF7D0', color: '#166534', fontSize: 11 }}>🟢 Live</span>;
    else if (q.status === 'Draft' || !q.is_active) statusBadge = <span className="badge" style={{ background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 11 }}>⚪ Draft</span>;
    else if (q.status === 'Expired') statusBadge = <span className="badge badge-expired" style={{ fontSize: 11 }}>🔴 Expired</span>;

    const dateDisplay = q.scheduled_date ? formatDateLabel(q.scheduled_date.slice(0, 10)) : 'No date set';

    return (
      <div key={q.id} className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div
          onClick={() => setViewQuote(q)}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
          title="Click to view full quote details"
        >
          <QuotePreview imageUrl={q.thumbnail_url || q.image_url} width={135} />
        </div>

        <div style={{ marginTop: 10, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
            {statusBadge}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
              <span title="Impressions / Views">👁️ {q.impression_count || 0}</span>
              <span title="Shares">↗ {q.share_count || 0}</span>
            </div>
          </div>

          <div
            style={{ fontSize: 13, fontWeight: 600, marginTop: 6, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={q.title || 'Untitled Quote'}
          >
            {q.title || <span className="text-muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>Untitled Quote</span>}
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            <MdCalendarMonth size={13} style={{ color: '#2563eb' }} />
            {dateDisplay}
          </div>
        </div>

        <div className="action-btns" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setViewQuote(q)}
            title="View Quote Details"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12, flex: 1, justifyContent: 'center' }}
          >
            <MdVisibility size={15} /> View
          </button>

          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setEditingQuote({
              ...q,
              scheduled_date: q.scheduled_date ? q.scheduled_date.slice(0, 10) : ''
            })}
            title="Edit Quote"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 12, flex: 1, justifyContent: 'center' }}
          >
            <MdEdit size={15} /> Edit
          </button>

          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => toggle(q)}
            title={q.is_active ? 'Active (click to deactivate)' : 'Inactive (click to activate)'}
            style={{ padding: '4px 6px' }}
          >
            {q.is_active ? <MdToggleOn className="text-green" size={22} /> : <MdToggleOff size={22} />}
          </button>

          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => setDeleteModal(q)}
            title="Delete Quote"
            style={{ padding: '4px 8px' }}
          >
            <MdDelete size={15} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Quotes</h2>
      </div>

      <div className="card">
        <div className="section-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h3 style={{ margin: 0 }}>Quote Cards</h3>
            {/* View Mode Switch */}
            <div style={{ display: 'inline-flex', background: 'var(--bg)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setViewMode('all')}
                style={{ padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 6 }}
              >
                <MdFormatListBulleted size={15} /> All Quotes ({allTotal || allQuotes.length})
              </button>
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'folders' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setViewMode('folders'); setSelectedFolder(null); }}
                style={{ padding: '4px 10px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 6 }}
              >
                <MdFolder size={15} /> By Date Folders
              </button>
            </div>
          </div>

          <div className="flex-row items-center gap-2">
            <select
              className="filter-select"
              value={filterStatus}
              onChange={e => handleStatusChange(e.target.value)}
              style={{ minWidth: 150 }}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Live">Live</option>
              <option value="Draft">Draft</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Expired">Expired</option>
            </select>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                if (!showForm && selectedFolder && selectedFolder !== 'evergreen') {
                  setForm(p => ({ ...p, scheduled_date: selectedFolder }));
                }
                setShowForm(!showForm);
              }}
            >
              {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Add Quote</>}
            </button>
          </div>
        </div>

        {showForm && (
          <form className="card form-card" onSubmit={create} style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {/* Left: live preview */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <QuotePreview imageUrl={form.image_url} width={170} />
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => uploadDesign(false)} disabled={uploading}>
                {uploading ? 'Uploading…' : (form.image_url ? 'Replace design' : 'Upload design')}
              </button>
              <span className="text-muted" style={{ fontSize: 11, textAlign: 'center', maxWidth: 180 }}>
                Required: <strong>2160 × 4200 px</strong>
              </span>
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                color: '#1e40af',
                background: '#dbeafe',
                padding: '2px 8px',
                borderRadius: 6
              }}>
                Strict 2160 × 4200 px
              </span>
            </div>

            {/* Right: fields */}
            <div style={{ flex: 1, minWidth: 240 }}>
              <div className="form-group">
                <label>Title (optional, internal)</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Monday Motivation" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Show on date * (visible all day)</label>
                  <input
                    type="date"
                    min={todayKey()}
                    value={form.scheduled_date}
                    onChange={e => set('scheduled_date', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Sort order</label>
                  <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} placeholder="0" />
                </div>
              </div>
              <label className="checkbox-label">
                <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
                Active (visible in app)
              </label>
              <button type="submit" className="btn btn-primary mt-md" disabled={uploading}>Add Quote</button>
            </div>
          </form>
        )}

        {viewMode === 'all' ? (
          /* ==================== ALL QUOTES LIST VIEW ==================== */
          <div style={{ marginTop: 16 }}>
            {allLoading ? (
              <p className="text-muted">Loading quotes…</p>
            ) : allQuotes.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {allQuotes.map(renderQuoteCard)}
              </div>
            ) : (
              <p className="text-muted" style={{ padding: '24px 0', textAlign: 'center' }}>
                {filterStatus ? `No quotes found with status "${filterStatus}".` : <>No quotes found. Click <strong>Add Quote</strong> above to create one.</>}
              </p>
            )}
          </div>
        ) : (
          /* ==================== DATE FOLDERS VIEW ==================== */
          loading ? <p className="text-muted" style={{ marginTop: 16 }}>Loading folders…</p> : selectedFolder === null ? (
            /* Folder list */
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 16, marginTop: 16 }}>
                {folders.map(({ date_key: dateKey, count }) => (
                  <div
                    key={dateKey}
                    className="card"
                    onClick={() => setSelectedFolder(dateKey)}
                    style={{ padding: 14, cursor: 'pointer', textAlign: 'center', transition: 'transform 0.15s ease' }}
                    title={`Open ${formatDateLabel(dateKey)}`}
                  >
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <MdFolder size={56} style={{ color: '#f59e0b' }} />
                      <span style={{
                        position: 'absolute', top: -6, right: -10, background: '#dc2626', color: '#fff',
                        borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 6px', lineHeight: '16px',
                      }}>{count}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>{formatDateLabel(dateKey)}</div>
                    <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{count} quote{count === 1 ? '' : 's'}</div>
                  </div>
                ))}
                {folders.length === 0 && (
                  <p className="text-muted" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '24px 0' }}>
                    {filterStatus ? `No quote folders found with status "${filterStatus}".` : 'No quote folders yet'}
                  </p>
                )}
              </div>
              {folders.length > 0 && (
                <Pagination pagination={foldersPagination} onPageChange={setFoldersPage} limit={foldersLimit} onLimitChange={setFoldersLimit} />
              )}
            </>
          ) : (
            /* Folder contents */
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-sm btn-ghost" onClick={() => setSelectedFolder(null)} style={{ marginBottom: 12 }}>
                <MdArrowBack /> All folders
              </button>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                paddingBottom: 8, borderBottom: '1px solid var(--border)',
              }}>
                <MdFolder style={{ color: '#f59e0b' }} size={20} />
                <strong style={{ fontSize: 15 }}>{formatDateLabel(selectedFolder)}</strong>
                <span className="text-muted" style={{ fontSize: 12 }}>({folderQuotes.length} quotes)</span>
              </div>
              {folderLoading ? <p className="text-muted">Loading…</p> : folderQuotes.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                  {folderQuotes.map(renderQuoteCard)}
                </div>
              ) : (
                <p className="text-muted" style={{ padding: '24px 0', textAlign: 'center' }}>
                  {filterStatus ? `No quotes in this folder match status "${filterStatus}".` : 'This folder is empty'}
                </p>
              )}
            </div>
          )
        )}

        {/* View Quote Details Modal */}
        {viewQuote && (
          <div className="modal-overlay" onClick={() => setViewQuote(null)}>
            <div className="modal" style={{ maxWidth: 620, minHeight: 480, padding: 0, overflow: 'hidden', borderRadius: 16, animation: 'none' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <h3 style={{ margin: 0, fontSize: 18 }}>Quote Details</h3>
                  <span className="badge" style={{
                    background: viewQuote.status === 'Live' ? '#BBF7D0' : viewQuote.status === 'Scheduled' ? '#FEF08A' : viewQuote.status === 'Expired' ? '#FEE2E2' : '#F3F4F6',
                    color: viewQuote.status === 'Live' ? '#166534' : viewQuote.status === 'Scheduled' ? '#854D0E' : viewQuote.status === 'Expired' ? '#991B1B' : '#374151',
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                    {viewQuote.status || (viewQuote.is_active ? 'Live' : 'Draft')}
                  </span>
                </div>
                <button className="modal-close" onClick={() => setViewQuote(null)}><MdClose /></button>
              </div>

              <div style={{ padding: 24, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {/* Left: Quote Image */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <QuotePreview imageUrl={viewQuote.image_url} width={180} />
                  <a
                    href={viewQuote.image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-ghost"
                    style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}
                  >
                    <MdOpenInNew size={14} /> Full Image (2160×4200)
                  </a>
                </div>

                {/* Right: Info Details */}
                <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Title
                    </label>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>
                      {viewQuote.title || <span className="text-muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>No title provided</span>}
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Scheduled Date
                    </label>
                    <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MdCalendarMonth size={16} style={{ color: '#2563eb' }} />
                      {formatDateLabel(viewQuote.scheduled_date ? viewQuote.scheduled_date.slice(0, 10) : 'evergreen')}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        Active in App
                      </label>
                      <div style={{ fontSize: 13, marginTop: 2, fontWeight: 600 }}>
                        {viewQuote.is_active ? '🟢 Active' : '⚪ Inactive'}
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        Sort Order
                      </label>
                      <div style={{ fontSize: 13, marginTop: 2, fontWeight: 600 }}>
                        {viewQuote.sort_order ?? 0}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        Impressions / Views
                      </label>
                      <div style={{ fontSize: 14, marginTop: 2, fontWeight: 600, color: 'var(--text)' }}>
                        👁️ {viewQuote.impression_count || 0} views
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        Shares
                      </label>
                      <div style={{ fontSize: 13, marginTop: 2 }}>
                        ↗ {viewQuote.share_count || 0} times shared
                      </div>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Dimensions
                    </label>
                    <div style={{ fontSize: 13, marginTop: 2, fontWeight: 600, color: '#16a34a' }}>
                      ✓ 2160 × 4200 px
                    </div>
                  </div>

                  {viewQuote.created_at && (
                    <div>
                      <label style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        Created At
                      </label>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {new Date(viewQuote.created_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer" style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    const q = viewQuote;
                    setViewQuote(null);
                    setEditingQuote({
                      ...q,
                      scheduled_date: q.scheduled_date ? q.scheduled_date.slice(0, 10) : ''
                    });
                  }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <MdEdit size={16} /> Edit This Quote
                </button>

                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setViewQuote(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Quote Modal */}
        {editingQuote && (
          <div className="modal-overlay" onClick={() => setEditingQuote(null)}>
            <div className="modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Quote</h3>
                <button className="modal-close" onClick={() => setEditingQuote(null)}><MdClose /></button>
              </div>
              <form onSubmit={saveEdit} style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '20px 24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <QuotePreview imageUrl={editingQuote.image_url} width={150} />
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => uploadDesign(true)} disabled={uploading}>
                    {uploading ? 'Uploading…' : 'Replace design'}
                  </button>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#1e40af', background: '#dbeafe', padding: '2px 8px', borderRadius: 6 }}>
                    Strict 2160 × 4200 px
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div className="form-group">
                    <label>Title (optional, internal)</label>
                    <input
                      value={editingQuote.title || ''}
                      onChange={e => setEditingQuote(p => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Monday Motivation"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Show on date *</label>
                      <input
                        type="date"
                        min={todayKey()}
                        value={editingQuote.scheduled_date || ''}
                        onChange={e => setEditingQuote(p => ({ ...p, scheduled_date: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Sort order</label>
                      <input
                        type="number"
                        value={editingQuote.sort_order ?? 0}
                        onChange={e => setEditingQuote(p => ({ ...p, sort_order: e.target.value }))}
                      />
                    </div>
                  </div>
                  <label className="checkbox-label" style={{ marginTop: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(editingQuote.is_active)}
                      onChange={e => setEditingQuote(p => ({ ...p, is_active: e.target.checked }))}
                    />
                    Active (visible in app)
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-ghost" onClick={() => setEditingQuote(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={uploading || savingEdit}>
                      {savingEdit ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal && (
          <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
            <div className="modal modal-sm" style={{ animation: 'none' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Quote</h3>
                <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this quote?</p>
                <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title || 'Untitled Quote'}"</p>
                <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
                <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
